import { CheckCircle2, AlertCircle, Loader2, Edit3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SaveStatus } from '../hooks/useAutoSave';

interface SaveStatusIndicatorProps {
  status: SaveStatus;
  className?: string;
  inline?: boolean;
}

export function SaveStatusIndicator({
  status,
  className = '',
  inline = false
}: SaveStatusIndicatorProps) {
  const { t } = useTranslation('common');

  const getStatusConfig = () => {
    switch (status) {
      case 'idle':
        return {
          icon: CheckCircle2,
          text: '',
          color: 'text-green-600',
          bgColor: '',
          showText: false,
        };
      case 'saved':
        return {
          icon: CheckCircle2,
          text: t('saveStatus.saved'),
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          showText: true,
        };
      case 'editing':
        return {
          icon: Edit3,
          text: t('saveStatus.editing'),
          color: 'text-gray-500',
          bgColor: 'bg-gray-50',
          showText: true,
        };
      case 'saving':
        return {
          icon: Loader2,
          text: t('saveStatus.saving'),
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          animate: true,
          showText: true,
        };
      case 'error':
        // Error: falha no salvamento
        return {
          icon: AlertCircle,
          text: t('saveStatus.error'),
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          showText: true,
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  // Para status 'idle' em modo inline, mostra apenas o ícone
  if (status === 'idle' && inline) {
    return (
      <div className={`flex items-center ${className}`}>
        <Icon
          size={16}
          className={config.color}
          aria-label={t('saveStatus.content_saved')}
        />
      </div>
    );
  }

  // Para outros status ou modo não-inline
  return (
    <div
      className={`
        flex items-center gap-1.5 text-xs
        ${inline ? 'px-0' : `px-2 py-1 rounded-md ${config.bgColor}`}
        ${className}
      `}
    >
      <Icon
        size={14}
        className={`${config.color} ${config.animate ? 'animate-spin' : ''}`}
        aria-hidden="true"
      />
      {config.showText && (
        <span className={config.color}>{config.text}</span>
      )}
    </div>
  );
}
